import { BaseModule } from './baseModule';
import type { 
  GoogleIdTokenPayload,
  AuthResponse,
  UserWithAdmin,
  MessageResponse,
  User,
  UpdateProfileRequest,
  Profile,
  ProfileResponse
} from './types';
import { TokenManager } from '../../utils/auth';

/**
 * Authentication Module - Handles all authentication-related operations
 * 
 * This module provides Google OAuth integration, user profile management,
 * and token management functionality.
 */
export class AuthModule extends BaseModule {
  
  /**
   * Authenticate user with Google ID token
   * 
   * @param idToken - Google ID token from OAuth flow
   * @returns Authentication response with JWT token and user data
   */
  async login(idToken: string): Promise<AuthResponse> {
    try {
      const payload: GoogleIdTokenPayload = { id_token: idToken };
      
      const response = await this.post<AuthResponse>(
        '/api/auth/google',
        payload,
        { skipAuth: true } // Skip auth for login endpoint
      );

      // Store the JWT token for future requests
      if (response.token) {
        TokenManager.setToken(response.token);
      }

      return response;
    } catch (error) {
      // Clear any existing tokens on login failure
      TokenManager.clearAll();
      throw error;
    }
  }

  /**
   * Logout user and clear authentication data
   * 
   * @returns Promise that resolves when logout is complete
   */
  async logout(): Promise<void> {
    try {
      // Notify server of logout
      await this.post<MessageResponse>('/api/auth/logout');
    } catch (error) {
      // Log error but continue with logout
      this.errorHandler.logError(
        this.errorHandler.handleError(error),
        'Server logout notification failed'
      );
    } finally {
      // Always clear local authentication data
      TokenManager.clearAll();
    }
  }

  /**
   * Get current authenticated user information
   * 
   * @returns User data with admin status
   */
  async getCurrentUser(): Promise<UserWithAdmin> {
    const response = await this.get<UserWithAdmin>('/api/me');
    return response;
  }

  /**
   * Refresh authentication token
   * 
   * Note: This method is primarily used by the AuthManager for automatic token refresh.
   * The backend doesn't currently have a dedicated refresh endpoint, so this method
   * will attempt to validate the current token by fetching user data.
   * 
   * @returns Promise that resolves when token is refreshed
   */
  async refreshToken(): Promise<void> {
    const currentToken = TokenManager.getToken();
    
    if (!currentToken || !TokenManager.isValidToken(currentToken)) {
      throw new Error('No valid token available for refresh');
    }

    try {
      // Validate current token by fetching user data
      await this.getCurrentUser();
      
      // If successful, the token is still valid
      // In a full implementation, this would call a dedicated refresh endpoint
    } catch {
      // Token is invalid, clear it
      TokenManager.clearAll();
      throw new Error('Token refresh failed - authentication required');
    }
  }

  /**
   * Check if user is currently authenticated
   * 
   * @returns True if user has a valid authentication token
   */
  isAuthenticated(): boolean {
    const token = TokenManager.getToken();
    return token !== null && TokenManager.isValidToken(token);
  }

  /**
   * Get current authentication token
   * 
   * @returns JWT token or null if not authenticated
   */
  getToken(): string | null {
    return TokenManager.getToken();
  }

  /**
   * Validate Google ID token format (client-side validation)
   * 
   * @param idToken - Google ID token to validate
   * @returns True if token format appears valid
   */
  private validateGoogleIdToken(idToken: string): boolean {
    if (!idToken || typeof idToken !== 'string') {
      return false;
    }

    // Basic JWT format validation (3 parts separated by dots)
    const parts = idToken.split('.');
    if (parts.length !== 3) {
      return false;
    }

    // Check that each part is base64url encoded
    try {
      parts.forEach(part => {
        if (!part) throw new Error('Empty JWT part');
        // Attempt to decode base64url
        const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
        atob(base64);
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Enhanced login with client-side validation
   * 
   * @param idToken - Google ID token from OAuth flow
   * @returns Authentication response with JWT token and user data
   */
  async loginWithValidation(idToken: string): Promise<AuthResponse> {
    // Validate token format before sending to server
    if (!this.validateGoogleIdToken(idToken)) {
      throw this.errorHandler.handleError(
        new Error('Invalid Google ID token format')
      );
    }

    return this.login(idToken);
  }

  /**
   * Get authentication status information
   * 
   * @returns Object containing authentication status details
   */
  getAuthStatus(): {
    isAuthenticated: boolean;
    hasToken: boolean;
    tokenValid: boolean;
    tokenExpired: boolean;
  } {
    const token = TokenManager.getToken();
    const hasToken = token !== null;
    const tokenValid = hasToken && TokenManager.isValidToken(token);
    const tokenExpired = hasToken && TokenManager.isTokenExpired(token);

    return {
      isAuthenticated: this.isAuthenticated(),
      hasToken,
      tokenValid,
      tokenExpired,
    };
  }

  /**
   * Update user profile data
   * 
   * @param userData - Partial user data to update
   * @returns Updated user data
   */
  async updateUser(userData: Partial<User>): Promise<User> {
    try {
      // Note: The backend doesn't have a dedicated user update endpoint
      // This would typically be handled through the profile update endpoint
      // For now, we'll return the updated user data optimistically
      const currentUser = await this.getCurrentUser();
      
      // In a real implementation, this would make an API call to update user data
      // For now, we'll merge the data locally
      const updatedUser: User = {
        ...currentUser,
        ...userData,
      };

      return updatedUser;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Get user profile information
   * 
   * @returns User profile data
   */
  async getUserProfile(): Promise<ProfileResponse> {
    try {
      const response = await this.get<ProfileResponse>('/api/profile');
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Update user profile information
   * 
   * @param profileData - Profile data to update
   * @returns Updated profile data
   */
  async updateUserProfile(profileData: UpdateProfileRequest): Promise<Profile> {
    try {
      const response = await this.put<Profile>('/api/profile', profileData);
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Check if current user has admin privileges
   * 
   * @returns True if user is an admin
   */
  async isUserAdmin(): Promise<boolean> {
    try {
      const userData = await this.getCurrentUser();
      return userData.is_admin || false;
    } catch {
      // If we can't fetch user data, assume not admin
      return false;
    }
  }

  /**
   * Get comprehensive user information including profile
   * 
   * @returns Complete user data with profile information
   */
  async getCompleteUserData(): Promise<{
    user: UserWithAdmin;
    profile?: Profile;
    authStatus: {
      isAuthenticated: boolean;
      hasToken: boolean;
      tokenValid: boolean;
      tokenExpired: boolean;
    };
  }> {
    try {
      const [userData, authStatus] = await Promise.all([
        this.getCurrentUser(),
        Promise.resolve(this.getAuthStatus()),
      ]);

      let profile: Profile | undefined;
      
      try {
        const profileResponse = await this.getUserProfile();
        profile = profileResponse.profile;
      } catch {
        // Profile might not exist yet, that's okay
        profile = undefined;
      }

      return {
        user: userData,
        profile,
        authStatus,
      };
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Validate user session and refresh if needed
   * 
   * @returns True if session is valid, false if authentication is required
   */
  async validateSession(): Promise<boolean> {
    try {
      const token = TokenManager.getToken();
      
      if (!token) {
        return false;
      }

      if (TokenManager.isTokenExpired(token)) {
        try {
          await this.refreshToken();
          return true;
        } catch {
          return false;
        }
      }

      // Validate token by fetching user data
      await this.getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Handle authentication errors with proper error formatting
   * 
   * @param error - Error from authentication operation
   * @returns Formatted error with user-friendly message
   */
  private handleAuthError(error: unknown): never {
    const apiError = this.errorHandler.handleError(error);
    
    // Provide user-friendly messages for common auth errors
    if (apiError.status === 401) {
      apiError.message = 'Authentication failed. Please try logging in again.';
    } else if (apiError.status === 403) {
      apiError.message = 'Access denied. You do not have permission to access this resource.';
    } else if (apiError.code === 'NETWORK_ERROR') {
      apiError.message = 'Unable to connect to authentication server. Please check your internet connection.';
    }

    throw apiError;
  }
}